import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'

interface VendasPorTipoData {
    tipo_id: string
    tipo_nome: string
    total_vendas: number
    valor_total: number
    valor_medio: number
    percentual: number
}

interface UseVendasPorTipoReturn {
    data: VendasPorTipoData[]
    loading: boolean
    error: string | null
    refetch: () => Promise<void>
    getVendasPorPeriodo: (dataInicio: string, dataFim: string) => Promise<VendasPorTipoData[]>
}

export function useVendasPorTipo(corretorId?: string): UseVendasPorTipoReturn {
    const [data, setData] = useState<VendasPorTipoData[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const fetchVendasPorTipo = async () => {
        try {
            setLoading(true)
            setError(null)

            console.log('🔄 Buscando dados de vendas por tipo...')
            console.log('🔄 Corretor ID recebido:', corretorId)

            // Se temos corretorId, verificar se é um user_id ou corretor_id
            if (corretorId) {
                console.log('🔍 Verificando se corretorId é user_id ou corretor_id...')

                // Buscar o corretor pelo user_id
                const { data: corretor, error: corretorError } = await supabase
                    .from('corretores')
                    .select('id, user_id')
                    .eq('user_id', corretorId)
                    .single()

                if (corretorError) {
                    console.log('❌ Erro ao buscar corretor por user_id:', corretorError)
                } else if (corretor) {
                    console.log('✅ Corretor encontrado:', corretor)
                    console.log('🔄 Usando corretor.id em vez de corretorId:', corretor.id)
                    // Usar o ID do corretor em vez do user_id
                    corretorId = corretor.id
                } else {
                    console.log('⚠️ Nenhum corretor encontrado para user_id:', corretorId)
                    // Talvez seja um corretor_id direto
                    console.log('🔄 Tentando usar corretorId como corretor_id direto')
                }
            }

            // Primeiro, tentar usar a view
            let vendasPorTipo = null
            let vendasError = null

            try {
                let query = supabase.from('vendas_por_tipo_imovel').select('*')

                // Se corretorId foi fornecido, filtrar por corretor
                if (corretorId) {
                    query = query.eq('corretor_id', corretorId)
                    console.log('🔍 Filtrando vendas por corretor:', corretorId)
                }

                const { data, error } = await query

                vendasPorTipo = data
                vendasError = error
                console.log('📊 Dados da view:', vendasPorTipo)
                console.log('❌ Erro da view:', vendasError)
            } catch (viewError) {
                console.log('⚠️ View não acessível, usando consulta direta...')
                vendasError = viewError
            }

            // Se a view falhou, usar consulta direta
            if (vendasError || !vendasPorTipo) {
                console.log('🔄 Executando consulta direta...')

                // Primeiro, buscar vendas simples (como no VGVGeralVendas)
                let vendasQuery = supabase
                    .from('vendas')
                    .select('id, valor_venda, corretor_id, unidade_id, imovel_id')

                // Se corretorId foi fornecido, filtrar por corretor
                if (corretorId) {
                    vendasQuery = vendasQuery.eq('corretor_id', corretorId)
                    console.log('🔍 Consulta direta: Filtrando vendas por corretor:', corretorId)
                }

                const { data: vendasData, error: vendasDataError } = await vendasQuery

                if (vendasDataError) {
                    console.error('❌ Erro na consulta de vendas:', vendasDataError)
                    throw vendasDataError
                }

                console.log('📊 Dados de vendas retornados:', vendasData)
                console.log('📊 Quantidade de vendas encontradas:', vendasData?.length || 0)

                // Buscar tipos de imóveis
                const { data: tiposData, error: tiposError } = await supabase
                    .from('tipos_imoveis')
                    .select('id, nome')

                if (tiposError) {
                    throw tiposError
                }

                console.log('📊 Tipos de imóveis:', tiposData)

                // Processar dados manualmente
                const vendasPorTipoMap = new Map()

                // Inicializar todos os tipos
                tiposData?.forEach(tipo => {
                    vendasPorTipoMap.set(tipo.id, {
                        tipo_id: tipo.id,
                        tipo_nome: tipo.nome,
                        total_vendas: 0,
                        valor_total_vendas: 0
                    })
                })

                // Para cada venda, buscar o tipo do imóvel
                for (const venda of vendasData || []) {
                    console.log(`🔍 Processando venda:`, venda)
                    
                    let imovel: any = null
                    
                    // Se tem unidade_id, buscar através da unidade
                    if (venda.unidade_id) {
                        const { data: unidadeData, error: unidadeError } = await supabase
                            .from('unidades')
                            .select(`
                                id,
                                imovel:imoveis!inner (
                                    tipo_id,
                                    tipo
                                )
                            `)
                            .eq('id', venda.unidade_id)
                            .single()

                        if (unidadeError || !unidadeData) {
                            console.log(`❌ Erro ao buscar unidade para venda ${venda.id}:`, unidadeError)
                            continue
                        }

                        console.log(`🔍 Unidade encontrada:`, unidadeData)
                        imovel = unidadeData.imovel as any
                    }
                    // Se não tem unidade_id mas tem imovel_id, buscar diretamente
                    else if (venda.imovel_id) {
                        const { data: imovelData, error: imovelError } = await supabase
                            .from('imoveis')
                            .select('id, tipo_id, tipo')
                            .eq('id', venda.imovel_id)
                            .single()

                        if (imovelError || !imovelData) {
                            console.log(`❌ Erro ao buscar imóvel para venda ${venda.id}:`, imovelError)
                            continue
                        }

                        console.log(`🔍 Imóvel encontrado diretamente:`, imovelData)
                        imovel = imovelData
                    }

                    if (!imovel) {
                        console.log(`❌ Imóvel não encontrado para venda ${venda.id}`)
                        continue
                    }

                    console.log(`🔍 Imóvel processado:`, imovel)

                    let tipoId = imovel.tipo_id
                    console.log(`🔍 Tipo ID do imóvel:`, tipoId)

                    // Se não tem tipo_id, tentar encontrar pelo nome do tipo
                    if (!tipoId && imovel.tipo) {
                        const tipoEncontrado = tiposData?.find(t => t.nome === imovel.tipo)
                        if (tipoEncontrado) {
                            tipoId = tipoEncontrado.id
                            console.log(`🔍 Tipo encontrado pelo nome:`, tipoEncontrado)
                        }
                    }

                    if (tipoId && vendasPorTipoMap.has(tipoId)) {
                        const tipo = vendasPorTipoMap.get(tipoId)
                        tipo.total_vendas += 1
                        tipo.valor_total_vendas += venda.valor_venda || 0
                        console.log(`✅ Venda associada: ${imovel.tipo} -> ${tipo.tipo_nome}`)
                    } else {
                        console.log(`⚠️ Venda não associada: ${imovel.tipo} (tipo_id: ${tipoId})`)
                        console.log(`⚠️ Tipos disponíveis:`, Array.from(vendasPorTipoMap.keys()))
                    }
                }

                vendasPorTipo = Array.from(vendasPorTipoMap.values())
                console.log('📊 Dados processados manualmente:', vendasPorTipo)
            }

            // Processar dados
            const totalVendas = vendasPorTipo?.reduce((sum, item) => sum + (item.total_vendas || 0), 0) || 0
            console.log('📈 Total de vendas:', totalVendas)

            const vendasProcessed: VendasPorTipoData[] = vendasPorTipo?.map(item => {
                const valorTotal = item.valor_total_vendas || 0
                const totalVendasItem = item.total_vendas || 0
                const valorMedio = totalVendasItem > 0 ? valorTotal / totalVendasItem : 0
                const percentual = totalVendas > 0 ? Math.round((totalVendasItem / totalVendas) * 100) : 0

                console.log(`📊 ${item.tipo_nome}: ${totalVendasItem} vendas, ${percentual}%`)

                return {
                    tipo_id: item.tipo_id,
                    tipo_nome: item.tipo_nome,
                    total_vendas: totalVendasItem,
                    valor_total: valorTotal,
                    valor_medio: valorMedio,
                    percentual: percentual
                }
            }) || []

            console.log('🎯 Dados processados:', vendasProcessed)

            // Ordenar por quantidade (maior primeiro)
            vendasProcessed.sort((a, b) => b.total_vendas - a.total_vendas)

            console.log('✅ Dados finais ordenados:', vendasProcessed)

            // Filtrar apenas tipos com vendas > 0 para o gráfico
            const vendasComVendas = vendasProcessed.filter(item => item.total_vendas > 0)
            console.log('🎯 Dados com vendas (para gráfico):', vendasComVendas)

            setData(vendasComVendas)
        } catch (err) {
            console.error('Erro ao buscar vendas por tipo:', err)
            setError(err instanceof Error ? err.message : 'Erro desconhecido')
        } finally {
            setLoading(false)
        }
    }

    const getVendasPorPeriodo = async (dataInicio: string, dataFim: string): Promise<VendasPorTipoData[]> => {
        try {
            const { data: vendasPorPeriodo, error } = await supabase
                .rpc('get_vendas_por_tipo', {
                    data_inicio: dataInicio,
                    data_fim: dataFim
                })

            if (error) {
                throw error
            }

            const totalVendas = vendasPorPeriodo?.reduce((sum: number, item: any) => sum + (item.total_vendas || 0), 0) || 0

            return vendasPorPeriodo?.map((item: any) => ({
                tipo_id: item.tipo_id,
                tipo_nome: item.tipo_nome,
                total_vendas: item.total_vendas || 0,
                valor_total: item.valor_total || 0,
                valor_medio: item.valor_medio || 0,
                percentual: totalVendas > 0 ? Math.round(((item.total_vendas || 0) / totalVendas) * 100) : 0
            })) || []
        } catch (err) {
            console.error('Erro ao buscar vendas por período:', err)
            return []
        }
    }

    useEffect(() => {
        fetchVendasPorTipo()
    }, [corretorId])

    return {
        data,
        loading,
        error,
        refetch: fetchVendasPorTipo,
        getVendasPorPeriodo
    }
}
